import type { SearchEntityType, SearchResultRow, WorkspaceRoutes } from '@immo/api-contract';

/**
 * Where a result row goes. Derived client-side from `entityType` + `id` (+
 * `parentId`) through the route catalog — the response deliberately carries no
 * `href`, because a workspace link needs the slug the catalog binds and the
 * backend has no business knowing it.
 *
 * `null` means the entity has no destination yet; the row is rendered but not
 * selectable, so a type can join the fan-out before its detail page exists.
 */
export function resultTarget(
  routes: WorkspaceRoutes,
  entityType: SearchEntityType,
  row: SearchResultRow,
): string | null {
  switch (entityType) {
    case 'kontakt':
      return routes.contact(row.id);
    case 'objekt':
      return routes.property(row.id);
    case 'deal':
      return routes.deal(row.id);
    case 'lead':
      return routes.lead(row.id);
    case 'dokument':
      // A Dokument has no route of its own — it is reached through the Objekt
      // it belongs to, which is what `parentId` carries, and specifically
      // through that Objekt's Dokumente tab (IMM-448.3).
      return row.parentId === null ? null : routes.property(row.parentId, 'documents');
  }
}

/**
 * Where a truncated group's „Alle Treffer in {Bereich}" row goes (IMM-448.4):
 * that entity's list, with the palette's query carried in `?q=` so the list
 * runs the same predicate the group's count came from. Built through the route
 * catalog, never assembled by hand — the catalog binds the workspace slug.
 *
 * `null` means the entity has NO list destination, which is why the row is
 * conditional on having somewhere to go rather than merely on being truncated.
 * Dokumente is the one such group: documents are only ever listed under an
 * Objekt, and the catalog has no workspace-level documents route at all. A
 * truncated Dokumente group still shows „5 von 12"; only the jump is absent.
 */
export function allResultsTarget(routes: WorkspaceRoutes, entityType: SearchEntityType, query: string): string | null {
  switch (entityType) {
    case 'kontakt':
      return routes.contacts(query);
    case 'objekt':
      return routes.properties(query);
    case 'deal':
      return routes.deals(query);
    case 'lead':
      return routes.leads(query);
    case 'dokument':
      return null;
  }
}
